// @flow

import moment, { type Moment } from "moment";

import {
  drop,
  mean,
  median,
  path,
  pathEq,
  prop,
  splitAt,
  sum,
  trim,
  zip,
  zipWith,
} from "ramda";

import YAML from "yaml";

import {
  formatCNPJ,
  formatDate,
  maybeEmpty,
  maybeParseFloat,
  orNull,
  parseDate,
  nullIfEmpty,
} from "../util";

import { type Medida } from "./model";
import { MODO_APP_SERIES } from "./constantes";
import { CASA_DECIMAL_PADRAO_NORMAL, CASA_DECIMAL_PADRAO_SERIES } from ".";

import Big from "big.js";

type AssetIdentification = {
  id: string,
  type: string,
};

type PGBL_VGBLIdentification = {
  id: string,
  fieId: ?string,
};

export type AssetKey = string;

export type AggFuncs = {
  avg: (Array<any>) => ?number,
  sum: (Array<any>) => ?number,
  median: (Array<any>) => ?number,
  max: (Array<any>) => ?number,
  min: (Array<any>) => ?number,
  maxDate: (Array<any>) => ?Moment,
  minDate: (Array<any>) => ?Moment,
};

export type ExportType = "xlsx" | "linked-xlsx" | "csv";

export type CurrentUser = {
  name: ?string,
  login: string,
  email: ?string,
};

export type ReportId = string;

type StringReportCol = {
  type: "string",
  medida: ?Medida,
  label: string,
};

type NumberReportCol = {
  type: "number",
  medida: ?Medida,
  label: string,
  prefix: ?string,
  suffix: ?string,
};

type PercentageReportCol = {
  type: "percentage",
  medida: ?Medida,
  label: string,
  prefix: ?string,
};

type MoneyReportCol = {
  type: "money",
  medida: ?Medida,
  label: string,
  currency: string,
  escala: number,
  casasDecimais: number,
};

type DateReportCol = {
  type: "date",
  medida: ?Medida,
  label: string,
  format: string,
};

export type ReportCol =
  | StringReportCol
  | NumberReportCol
  | PercentageReportCol
  | MoneyReportCol
  | DateReportCol;

export type ReportRow = {
  key: string,
  values: Array<string | number>,
};

export type ReportGridRow = ReportRow & { rowId: string };

export type Report = {
  id: ReportId,
  cols: Array<ReportCol>,
  rows: Array<ReportRow>,
  configuration: Object,
  metadata: Object,
  visualizacao: ?Object,
};

export const isNameColumn = pathEq(["medida", "nome"], "Nome");

const filterOnlyNumbers: (Array<any>) => Array<number> = values =>
  values
    .filter(n => n != null && String(n).trim() !== "")
    .map(parseFloat)
    .filter(n => !isNaN(n));

const filterValidDates: (Array<any>) => Array<Moment> = values =>
  values.map(date => moment(date, "DD/MM/YYYY")).filter(date => date.isValid());

export const aggregationFunctions: AggFuncs = {
  avg: values => nullIfEmpty(filterOnlyNumbers(values), orNull(mean)),

  count: prop("length"),

  sum: values => nullIfEmpty(filterOnlyNumbers(values), sum),

  median: values => nullIfEmpty(filterOnlyNumbers(values), orNull(median)),

  max: values => {
    const numbers = filterOnlyNumbers(values);
    return numbers.length > 0 ? Math.max(...numbers) : null;
  },

  min: values => {
    const numbers = filterOnlyNumbers(values);
    return numbers.length > 0 ? Math.min(...numbers) : null;
  },

  maxDate: values => {
    const validDates = filterValidDates(values);
    switch (validDates.length) {
      case 0:
        return null;
      case 1:
        return validDates[0];
      default:
        return validDates.reduce((min, date) => moment.max(min, date));
    }
  },

  minDate: values => {
    const validDates = filterValidDates(values);
    switch (validDates.length) {
      case 0:
        return null;
      case 1:
        return validDates[0];
      default:
        return validDates.reduce((min, date) => moment.min(min, date));
    }
  },
};

export const defaultColDef = {
  resizable: true,
};

export const encodeAssetKey: AssetIdentification => AssetKey = ({ id, type }) =>
  [id, type].join("♥♥♥");

export const decodeAssetKey: AssetKey => AssetIdentification = key => {
  const [id, type] = key.split("♥♥♥");
  return { id, type };
};

export const decodePGBLVGBLFieId: string => PGBL_VGBLIdentification = key => {
  const [pgblVgblId, fieId] = key.split("|");
  return { id: pgblVgblId, fieId };
};

const normalizaValoresResultado = (headers, rows) =>
  rows.map(row =>
    zipWith(
      (value, { formatacao }) => {
        switch (formatacao) {
          case "PORCENTAGEM_TAXAS":
            return maybeEmpty(String(value).trim())
              .map(value => value.replace("%", "").replace(",", "."))
              .map(value =>
                maybeParseFloat(value)
                  .map(number => new Big(number).div(100).toString())
                  .getOrElse(value)
              )
              .getOrElse(null);

          case "CNPJ":
            return maybeEmpty(value)
              .map(s => String(s).trim())
              .map(formatCNPJ)
              .getOrElse(null);

          case "DATA":
            return maybeEmpty(value)
              .map(trim)
              .chain(date => parseDate(date))
              .map(date => formatDate(date))
              .getOrElse(
                maybeEmpty(value)
                  .map(trim)
                  .getOrElse(null)
              );

          default:
            return maybeEmpty(value)
              .map(s => {
                // Caso seja recebido um objeto no lugar de valor, significa que esse deve ter sua
                // formatação diferente, baseado na sua chave "formatacaoResultado" e não na coluna.
                // Passar o objeto como metadado de uma String é necessário para que o ag-grid continue
                // enxergando o valor original como string.
                if (typeof s === "object") {
                  const objCompleto = s;
                  // eslint-disable-next-line
                  const valor = new Number(s.valor);
                  // $FlowFixMe
                  valor.__metadados__ = objCompleto;
                  return valor;
                } else {
                  return String(s).trim();
                }
              })
              .getOrElse(null);
        }
      },
      row,
      headers
    )
  );

// Em relatórios de séries as tres primeiras colunas não são medidas, e sim identificadores,
// e a quarta coluna é a data. Aqui configuramos o comportamento de cada uma dessas colunas.
const colunaSeriesCodigo = { buscaGlobal: false };
const colunaSeriesCNPJ = { buscaGlobal: true };
const colunaSeriesNome = { buscaGlobal: true };
const colunaSeriesData = { buscaGlobal: false };
const colunasExtrasSeries = [
  colunaSeriesCodigo,
  colunaSeriesCNPJ,
  colunaSeriesNome,
  colunaSeriesData,
];

export const buscarRelatorio = (api: any) => async (
  id: ReportId
): Promise<Report> => {
  const report = await api.reportResultConfiguration({ id });
  const isModoSeries = report.tipoRelatorio === MODO_APP_SERIES;
  const casasDecimaisDefault = isModoSeries
    ? CASA_DECIMAL_PADRAO_SERIES
    : CASA_DECIMAL_PADRAO_NORMAL;

  const visualizacao = await api
    .reportVisualization({ id })
    .catch(error =>
      error.response.status === 404 ? null : Promise.reject(error)
    );

  const metadata = await api.reportRows({ id });
  const { headers, valores, ativos } = metadata;

  const assets = ativos
    .map(({ identificador: id, tipoItemSelecionavel: type }) => ({
      id,
      type,
    }))
    .map(encodeAssetKey);

  const [[colNames], values] = splitAt(1, valores);

  const medidasSelecionadas = report.medidasSelecionadas.map(
    ({ buscaGlobal, ...medida }) => ({
      medida,
      buscaGlobal,
    })
  );

  const medidasColunas = isModoSeries
    ? [...colunasExtrasSeries, ...medidasSelecionadas]
    : medidasSelecionadas;

  const cols = zipWith(
    ([{ medida, buscaGlobal = false }, label], measurement) => {
      const base = {
        medida,
        label,
        alias: measurement.alias,
        globalSearch: buscaGlobal,
      };

      switch (measurement.formatacao) {
        case "PORCENTAGEM":
        case "PORCENTAGEM_TAXAS_SEM_TEXTOS":
        case "PORCENTAGEM_TAXAS":
          return {
            ...base,
            type: "percentage",
            casasDecimais:
              medida.casasDecimais !== null ||
              medida.casasDecimais !== undefined
                ? medida.casasDecimais
                : casasDecimaisDefault,
          };
        case "DECIMAL":
          return {
            ...base,
            type: "number",
            casasDecimais:
              medida.casasDecimais !== null ||
              medida.casasDecimais !== undefined
                ? medida.casasDecimais
                : casasDecimaisDefault,
          };
        case "MOEDA":
        case "MOEDA_QUALITATIVA":
          return {
            ...base,
            type: "money",
            currency: measurement.moeda,
            escala: medida.escala ? medida.escala : null,
            casasDecimais:
              medida.casasDecimais !== null ||
              medida.casasDecimais !== undefined
                ? medida.casasDecimais
                : casasDecimaisDefault,
          };
        case "INTEIRO":
          return { ...base, type: "number", casasDecimais: 0 };
        case "DATA":
          return { ...base, type: "date", format: "DD/MM/YYYY" };
        default:
          return { ...base, type: "string" };
      }
    },
    zip(medidasColunas, colNames),
    headers
  );

  const normalizedValues = normalizaValoresResultado(headers, values);

  const rows = zipWith(
    (key, values) => ({ key, values }),
    assets,
    normalizedValues
  );

  return {
    id,
    cols,
    rows,
    metadata,
    visualizacao,
    configuration: report,
  };
};

export const adicionaAtivosAoRelatorio = (api: any) => async (
  relatorio: Report,
  ativos: Array<AssetKey>
) => {
  const { id, configuration } = relatorio;

  const newConfiguration = await api
    .gerarRelatorio({
      ativosSelecionados: [],
      ...configuration,
    })
    .then(uuid => api.reportConfiguration({ id: uuid }));

  const metadata = await api.generateReport({
    ...newConfiguration,
    itemSelecionavel: YAML.stringify(
      ativos.map(decodeAssetKey).map(({ id, type }) => ({
        identificador: id,
        tipoItemSelecionavel: type,
      }))
    ),
  });

  const ativosResultado = metadata.ativos
    .map(({ identificador: id, tipoItemSelecionavel: type }) => ({
      id,
      type,
    }))
    .map(encodeAssetKey);

  const rows = zipWith(
    (key, values) => ({ key, values }),
    ativosResultado,
    normalizaValoresResultado(metadata.headers, drop(1, metadata.valores))
  );

  await api.mergeReports({
    sourceId: metadata.idResultado,
    destinationId: id,
  });

  return {
    rows,
    metadata,
  };
};

export const colunaExibeMinimoCasasDecimais = (col: any) =>
  path(["medida", "medida", "grupamentoMedida", "nome"], col) ===
    "QUALITATIVA" && path(["type"], col) === "percentage";

export const ajustarCasasDecimais = (col: any, valoresCol: any) => {
  const minimoCasasDecimaisColuna = valoresColuna => {
    let minimoCasas = 0;
    for (let valor of valoresColuna) {
      try {
        valor = Number(valor).toString();
        if (valor.includes(".")) {
          minimoCasas = Math.max(minimoCasas, valor.split(".")[1].length);
        }
      } catch (ignored) {}
    }
    return minimoCasas;
  };

  col.casasDecimais = minimoCasasDecimaisColuna(valoresCol);
  if (col.type === "percentage") {
    col.casasDecimais = Math.max(col.casasDecimais - 2, 0);
  }
};
